import React, { useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";

import PageTitle from "components/generic/PageTitle";
import QuickActionCard from "components/generic/QuickActions";
import Breadcrumbs from 'components/generic/Breadcrumbs';
import SuccessfulSubmission from "components/generic/SuccessfulSubmission";

import MemberForm from "components/crewManagement/MemberForm";
import UploadResumePortfolio from "components/crewManagement/UploadResumePortfolio";
import AvailabilityChecklist from "components/crewManagement/AvailabilityChecklist";
import UploadProfilePhoto from "components/crewManagement/UploadProfilePhoto";

import { postCreateCrewMember } from "lib/api/crewApi";

const AddCrewMemberPage = () => {
  const formRef = useRef(null);
  const isDraftRef = useRef(false);

  const [availability, setAvailability] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [resume, setResume] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [certificationFiles, setCertificationFiles] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (formData) => {
  setSubmitError(null);

  try {
    const fd = new FormData();

    const user = JSON.parse(localStorage.getItem('user')) || {};
    const organizationType = user?.organizationType;

    // Append other form data
    Object.entries(formData || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      if (Array.isArray(value) || typeof value === "object") {
        fd.append(key, JSON.stringify(value));
      } else {
        fd.append(key, String(value));
      }
    });

    fd.append("availability", JSON.stringify(availability || []));

    if (organizationType === 2) {
      fd.append("is_beige_member", "0");
    }

    if (isDraftRef.current) {
      fd.append("is_draft", "1");
    }

    if (profileImage?.file) fd.append("profile_photo", profileImage.file);
    if (resume?.file) fd.append("resume", resume.file);
    if (portfolio?.file) fd.append("portfolio", portfolio.file);
    (certificationFiles || []).forEach((f) => {
      if (f) fd.append("certifications", f);
    });

    const response = await postCreateCrewMember(fd);
    console.log("Crew Member added:", response);

    const wasDraft = isDraftRef.current;
    isDraftRef.current = false;

    if (!wasDraft) {
      setIsComplete(true);
    }

  } catch (err) {
    console.error("Crew Member create error", err);
    setSubmitError(err.message || "Failed to create crew member.");
  }
};


  const handleExternalSubmit = () => {
    isDraftRef.current = false;
    formRef.current?.requestSubmit();
  };

  const handleSaveDraft = () => {
    isDraftRef.current = true;
    formRef.current?.requestSubmit();
  };

  return (
    <div className="add-crew-content p-6">
      {isComplete ? (
        <SuccessfulSubmission
          title={"Crew Member Successfully Added"}
          description={"A new team member has been added to your crew pool."}
          buttonText={"Go to Crew Management"}
          buttonLink={"/crew-management"}
        />
      ) : (
        <>
          <Breadcrumbs />
          <PageTitle
            title={"Add New Crew Member"}
            description={"Add a new team member to your crew pool"}
          />

          {submitError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {submitError}
            </div>
          )}

          <div className="flex gap-6 self-stretch">
            <div className="w-4/6">
              <MemberForm
                passRef={formRef}
                handleSubmit={handleSubmit}
                certifications={certificationFiles}
                setCertifications={setCertificationFiles}
              />
            </div>

            <div className="w-2/6 flex flex-col gap-6">
              <QuickActionCard
                handleClick={handleExternalSubmit}
                buttonText={"Add Crew Member"}
                icon={<CheckCircle2 className="w-4 h-4" />}

                onDraftClick={handleSaveDraft}
              />

              <UploadProfilePhoto photo={profileImage} setPhoto={setProfileImage} />
              <AvailabilityChecklist value={availability} onChange={setAvailability} />
              <UploadResumePortfolio
                resume={resume}
                setResume={setResume}
                portfolio={portfolio}
                setPortfolio={setPortfolio}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddCrewMemberPage;
